import type { ClientDTO, ClientTimeline, TimelineDelivery, TimelineReport } from '@brainkey/shared';

import { reportTypeLabel } from './format';

/**
 * Pure derivations over the `useActivityAggregate()` result — separated out
 * so they're testable without React/TanStack Query. Compares against literal
 * wire status strings (`'GENERATED'`, `'DELIVERED'`), not the (currently
 * mistyped) shared `ReportStatus` union — see DEC-0023.
 */

/** Delivery statuses that count as "sent" — kept in sync by hand with the
 * equivalent constant in services/api/src/modules/reports/report.prisma-repository.ts
 * (no shared constant crosses the Node/React boundary today). P13-T02/DEC-0058. */
const SENT_DELIVERY_STATUSES = new Set(['SENT', 'DELIVERED', 'READ']);

/** Client-side counterpart of the archive's server-side `sent` join
 * (`ReportListItemDTO.sent`) — used on the client-detail page's Reports tab,
 * which already has the full timeline (assessments+reports+deliveries)
 * fetched together and doesn't need a second network round-trip. */
export function reportIsSent(report: TimelineReport, deliveries: TimelineDelivery[]): boolean {
  return deliveries.some(
    (d) => d.reportId === report.id && d.kind === 'REPORT' && SENT_DELIVERY_STATUSES.has(d.status),
  );
}

export interface ReportToSend {
  client: ClientDTO;
  report: TimelineReport;
}

export type ActivityKind = 'assessment' | 'report' | 'questionnaire' | 'delivery' | 'note';

export interface ActivityEntry {
  clientId: string;
  clientName: string;
  kind: ActivityKind;
  label: string;
  at: Date;
}

function fullClientName(client: ClientDTO): string {
  return `${client.firstName} ${client.lastName}`;
}

/** Clients with at least one assessment but no delivered report yet. */
export function countClientsInProgress(
  clients: ClientDTO[],
  timelines: Map<string, ClientTimeline>,
): number {
  let count = 0;
  for (const client of clients) {
    const timeline = timelines.get(client.id);
    if (!timeline) continue;
    const hasAssessment = timeline.assessments.length > 0;
    const hasDeliveredReport = timeline.reports.some((r) => r.status === 'DELIVERED');
    if (hasAssessment && !hasDeliveredReport) count += 1;
  }
  return count;
}

/** Questionnaires dispatched but not yet responded to (SENT or DELIVERED). */
export function countPendingResponses(timelines: Map<string, ClientTimeline>): number {
  let count = 0;
  for (const timeline of timelines.values()) {
    count += timeline.questionnaires.filter(
      (q) => q.dispatchStatus === 'SENT' || q.dispatchStatus === 'DELIVERED',
    ).length;
  }
  return count;
}

/** Generated-but-not-yet-delivered reports, oldest-waiting first. */
export function findReportsToSend(
  clients: ClientDTO[],
  timelines: Map<string, ClientTimeline>,
): ReportToSend[] {
  const results: ReportToSend[] = [];
  for (const client of clients) {
    const timeline = timelines.get(client.id);
    if (!timeline) continue;
    for (const report of timeline.reports) {
      if (report.status === 'GENERATED') results.push({ client, report });
    }
  }
  results.sort((a, b) => {
    const aAt = new Date(a.report.generatedAt ?? a.report.createdAt).getTime();
    const bAt = new Date(b.report.generatedAt ?? b.report.createdAt).getTime();
    return aAt - bAt;
  });
  return results;
}

/** Merges all 5 timeline arrays across every aggregated client, newest-first. */
export function buildRecentActivity(
  clients: ClientDTO[],
  timelines: Map<string, ClientTimeline>,
  limit = 8,
): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  for (const client of clients) {
    const timeline = timelines.get(client.id);
    if (!timeline) continue;
    const clientName = fullClientName(client);

    for (const a of timeline.assessments) {
      entries.push({
        clientId: client.id,
        clientName,
        kind: 'assessment',
        label: 'Assessment captured',
        at: new Date(a.createdAt),
      });
    }
    for (const r of timeline.reports) {
      entries.push({
        clientId: client.id,
        clientName,
        kind: 'report',
        // STANDARD-variant (Brain Skills) reports have no meaningful
        // audience cut to show — lead with the report type instead.
        label:
          r.variant === 'STANDARD'
            ? `${reportTypeLabel(r.reportType)} report (${r.language})`
            : `Report · ${r.variant} (${r.language})`,
        at: new Date(r.generatedAt ?? r.createdAt),
      });
    }
    for (const q of timeline.questionnaires) {
      entries.push({
        clientId: client.id,
        clientName,
        kind: 'questionnaire',
        label: `Questionnaire · ${q.type} v${q.version}`,
        at: new Date(q.sentAt ?? q.createdAt),
      });
    }
    for (const d of timeline.deliveries) {
      entries.push({
        clientId: client.id,
        clientName,
        kind: 'delivery',
        label: `Delivery · ${d.kind} via ${d.channel}`,
        at: new Date(d.deliveredAt ?? d.sentAt ?? d.createdAt),
      });
    }
    for (const n of timeline.notes) {
      entries.push({
        clientId: client.id,
        clientName,
        kind: 'note',
        label: 'Note added',
        at: new Date(n.createdAt),
      });
    }
  }

  entries.sort((a, b) => b.at.getTime() - a.at.getTime());
  return entries.slice(0, limit);
}
